import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Info, 
  X, 
  Gamepad2,
  Download,
  RefreshCw,
  Trash2,
  Lock,
  AlertCircle as AlertIcon
} from 'lucide-react';
import { Toast as ToastType } from '../../types/toast';
import { useToast } from '../../contexts/ToastContext';

interface ToastProps {
  toast: ToastType;
}

const getToastStyles = (type: ToastType['type']) => {
  switch (type) {
    case 'success':
      return {
        bg: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
        icon: 'text-green-600 dark:text-green-400',
        title: 'text-green-900 dark:text-green-100',
        message: 'text-green-700 dark:text-green-200'
      };
    case 'error':
      return {
        bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
        icon: 'text-red-600 dark:text-red-400',
        title: 'text-red-900 dark:text-red-100',
        message: 'text-red-700 dark:text-red-200'
      };
    case 'warning':
      return {
        bg: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
        icon: 'text-yellow-600 dark:text-yellow-400',
        title: 'text-yellow-900 dark:text-yellow-100',
        message: 'text-yellow-700 dark:text-yellow-200'
      };
    case 'info':
      return {
        bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
        icon: 'text-blue-600 dark:text-blue-400',
        title: 'text-blue-900 dark:text-blue-100',
        message: 'text-blue-700 dark:text-blue-200'
      };
    default:
      return {
        bg: 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800',
        icon: 'text-gray-600 dark:text-gray-400',
        title: 'text-gray-900 dark:text-gray-100',
        message: 'text-gray-700 dark:text-gray-200'
      };
  }
};

const getDefaultIcon = (type: ToastType['type']) => {
  switch (type) {
    case 'success':
      return CheckCircle;
    case 'error':
      return XCircle;
    case 'warning':
      return AlertTriangle;
    case 'info':
      return Info;
    default:
      return Info;
  }
};

const getCustomIcon = (iconName?: string) => {
  switch (iconName) {
    case '✅':
    case 'check':
      return CheckCircle;
    case '🎮':
    case 'gamepad':
      return Gamepad2;
    case '📥':
    case 'download':
      return Download;
    case '🗑️':
    case 'trash':
      return Trash2;
    case '🔒':
    case 'lock':
      return Lock;
    case '⚠️':
    case 'warning':
      return AlertTriangle;
    case '🔄':
    case 'refresh':
      return RefreshCw;
    default:
      return null;
  }
};

const ToastComponent: React.FC<ToastProps> = ({ toast }) => {
  const { dismissToast } = useToast();
  const toastRef = useRef<HTMLDivElement>(null);
  const styles = getToastStyles(toast.type);
  
  const IconComponent = getCustomIcon(toast.icon) || getDefaultIcon(toast.type);

  const handleDismiss = () => {
    if (toastRef.current) {
      gsap.to(toastRef.current, {
        x: 100,
        opacity: 0,
        scale: 0.95,
        duration: 0.2,
        ease: 'power2.in',
        onComplete: () => dismissToast(toast.id)
      });
    } else {
      dismissToast(toast.id);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleDismiss();
    }
  };

  // Auto-dismiss setup is handled in ToastContext
  return (
    <div
      ref={toastRef}
      className={`toast-item relative p-4 rounded-lg border shadow-lg backdrop-blur-sm ${styles.bg}`}
      role="alert"
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`flex-shrink-0 mt-0.5 ${styles.icon}`}>
          <IconComponent className="w-5 h-5" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className={`text-sm font-semibold ${styles.title}`}>
            {toast.title}
          </h4>
          <p className={`mt-1 text-sm ${styles.message}`}>
            {toast.message}
          </p>
          
          {/* Actions */}
          {toast.actions && toast.actions.length > 0 && (
            <div className="mt-3 flex gap-2">
              {toast.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.action}
                  className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${
                    toast.type === 'success' 
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : toast.type === 'error'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : toast.type === 'warning'
                      ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className={`flex-shrink-0 p-1 rounded-md transition-colors hover:bg-black/10 dark:hover:bg-white/10 ${styles.icon}`}
          aria-label="Dismiss notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* Progress bar for auto-dismiss */}
      {toast.duration && toast.duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10 dark:bg-white/10 rounded-b-lg overflow-hidden">
          <div 
            className={`h-full transition-all ease-linear ${
              toast.type === 'success' ? 'bg-green-600' :
              toast.type === 'error' ? 'bg-red-600' :
              toast.type === 'warning' ? 'bg-yellow-600' :
              'bg-blue-600'
            }`}
            style={{
              animation: `shrink ${toast.duration}ms linear forwards`
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ToastComponent;
